import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { authRedirectService, type StoredMeetingHash } from '../services/authRedirectService';
import { useUser } from './useUserData';

export type { StoredMeetingHash };

export function useMeetingLink() {
  const params = useParams<{ meetingId?: string }>();
  const location = useLocation();

  const { isAuthenticated } = useUser();

  const resolvedMeetingId = useMemo(() => {
    return params.meetingId || getMeetingIdFromPath(location.pathname) || null;
  }, [params.meetingId, location.pathname]);

  useEffect(() => {
    if (!(resolvedMeetingId && location.hash)) {
      return;
    }

    const cleanHash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;

    const payload: StoredMeetingHash = {
      meetingId: resolvedMeetingId,
      isPending: !isAuthenticated,
      hash: cleanHash,
    };

    authRedirectService.saveMeetingHashParams(payload);

    window.history.replaceState(null, '', location.pathname);
  }, [resolvedMeetingId, location.hash, location.pathname, isAuthenticated]);

  const parsed = useMemo(() => {
    let hashString = '';

    if (location.hash) {
      hashString = location.hash.slice(1);
    } else if (resolvedMeetingId) {
      const storedHash = authRedirectService.getStoredMeetingHash(resolvedMeetingId);
      if (storedHash) {
        hashString = storedHash;
      }
    }

    let password = '';
    let foundPassword = false;
    const extraParams: Record<string, string> = {};

    const parts = hashString.split('&');

    parts.forEach((item) => {
      const eq = item.indexOf('=');
      if (eq === -1) {
        if (!foundPassword) {
          password = decodeURIComponent(item);
          foundPassword = true;
        }
        return;
      }

      const [rawKey, rawValue] = item.split('=');
      if (!rawKey) {
        return;
      }

      const key = rawKey.startsWith('config.') ? rawKey.substring('config.'.length) : rawKey;

      extraParams[key] = rawValue ? decodeURIComponent(rawValue) : '';
    });

    return { password, extraParams };
  }, [resolvedMeetingId, location.hash]);

  return {
    meetingId: resolvedMeetingId,
    password: parsed.password,
    extraParams: parsed.extraParams,
  };
}

/* ---------- helpers ---------- */

function getMeetingIdFromPath(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean);
  return parts.length ? parts[0] : null;
}
